<template>
  <div class="edit-meeting-page">
    <div class="app-block" v-if="meeting || loading">
      <div class="app-block-title">
        <FeatherIcon type="edit" size="24"></FeatherIcon>
        <h1>Modifier Réunion</h1>
      </div>

      <p v-if="loading">Chargement des données de la réunion...</p>
      <p v-else-if="fetchError" class="error-message">{{ fetchError }}</p>

      <form v-else @submit.prevent="updateMeeting">
        <div class="form-group">
          <label for="title"
            >Titre de la réunion <span class="required">*</span></label
          >
          <input
            type="text"
            id="title"
            v-model="meeting.title"
            required
            :disabled="saveLoading"
          />
        </div>

        <div class="form-group">
          <label for="date">Date <span class="required">*</span></label>
          <input
            type="date"
            id="date"
            v-model="meeting.meeting_date"
            required
            :disabled="saveLoading"
          />
        </div>

        <div class="form-group">
          <label for="time">Heure</label>
          <input
            type="time"
            id="time"
            v-model="meeting.meeting_time"
            :disabled="saveLoading"
          />
        </div>

        <div class="form-group">
          <label for="notes">Notes</label>
          <textarea
            id="notes"
            v-model="meeting.notes"
            :disabled="saveLoading"
          ></textarea>
        </div>

        <div class="form-actions">
          <BaseButton
            type="submit"
            variant="primary"
            :loading="saveLoading"
            :disabled="saveLoading"
          >
            <FeatherIcon
              v-if="saveLoading"
              type="loader"
              size="18"
              animation="spin"
            ></FeatherIcon>
            <FeatherIcon v-else type="save" size="18"></FeatherIcon>
            Enregistrer les modifications
          </BaseButton>
          <BaseButton
            type="button"
            variant="secondary"
            @click="cancel"
            :disabled="saveLoading"
          >
            Annuler
          </BaseButton>
          <!-- FUTURE: Bouton Supprimer ici? Ou sur la page de détail -->
        </div>
        <p v-if="saveError" class="error-message">{{ saveError }}</p>
      </form>
    </div>
    <div class="app-block message-block" v-else>
      <p>Réunion non trouvée ou erreur de chargement.</p>
      <div class="back-button-container">
        <BaseButton variant="secondary" @click="$router.push('/')">
          <FeatherIcon type="arrow-left" size="16"></FeatherIcon> Retour à la
          liste
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/BaseButton.vue";
import FeatherIcon from "vue-feather";
import apiService from "@/services/apiService";

export default {
  name: "EditMeetingView",
  components: {
    BaseButton,
    FeatherIcon,
  },
  props: {
    id: {
      // L'ID de la réunion à modifier, passé par le routeur
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      meeting: null, // Les données de la réunion à éditer
      loading: false, // Pour le chargement initial des données
      fetchError: null, // Erreur lors du chargement initial
      saveLoading: false, // Pour l'enregistrement des modifications
      saveError: null, // Erreur lors de l'enregistrement
    };
  },
  created() {
    this.fetchMeeting(); // Charger les données de la réunion au chargement de la page
  },
  methods: {
    async fetchMeeting() {
      this.loading = true;
      this.fetchError = null;
      try {
        const data = await apiService.getMeeting(this.id);
        this.meeting = data;
        // Assurez-vous que meeting_time est un string vide si NULL, pour le input type="time"
        if (this.meeting && this.meeting.meeting_time === null) {
          this.meeting.meeting_time = "";
        }
      } catch (e) {
        this.fetchError =
          "Impossible de charger les données de la réunion: " + e.message;
        console.error("Fetching meeting for edit failed:", e);
      } finally {
        this.loading = false;
      }
    },
    async updateMeeting() {
      if (!this.meeting.title || !this.meeting.meeting_date) {
        this.saveError = "Veuillez remplir les champs requis.";
        return;
      }

      this.saveLoading = true;
      this.saveError = null;

      try {
        // Créer un objet avec les données à envoyer (seulement les champs du formulaire)
        const updateData = {
          title: this.meeting.title,
          meeting_date: this.meeting.meeting_date,
          meeting_time:
            this.meeting.meeting_time === "" ? null : this.meeting.meeting_time, // Envoyer null si le champ heure est vide
          notes: this.meeting.notes === "" ? null : this.meeting.notes, // Envoyer null si les notes sont vides
        };

        const updatedMeeting = await apiService.updateMeeting(
          this.id,
          updateData
        );
        console.log("Meeting updated:", updatedMeeting);

        // Rediriger vers la page de détail après succès
        this.$router.push({ name: "MeetingDetail", params: { id: this.id } });
      } catch (e) {
        this.saveError = "Erreur lors de l'enregistrement: " + e.message;
        console.error("Update meeting failed:", e);
      } finally {
        this.saveLoading = false;
      }
    },
    cancel() {
      // Retourner à la page de détail (ou à la liste si on venait de là)
      this.$router.go(-1); // Revenir à la page précédente
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/css/_variables.scss";

.edit-meeting-page {
  max-width: 600px; // Même largeur que la page de création
  margin: $space-xl auto;
  padding: 0 $space-md;
}

// Copie des styles de base des blocs (identiques à NewMeetingView et MeetingDetailView)
.app-block {
  background-color: $color-surface;
  padding: $space-lg;
  margin-bottom: $space-lg;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
}

.app-block-title {
  font-family: $font-heading;
  font-size: $font-size-xl;
  color: $color-text-900;
  margin-bottom: $space-lg;
  padding-bottom: $space-sm;
  border-bottom: 1px solid $color-primary-100;
  display: flex;
  align-items: center;

  .feather-icon {
    margin-right: $space-sm;
    color: $color-primary-500; // Icône bleue par défaut
  }
  .feather-icon[type="edit"] {
    color: $color-accent-500; // Icône d'édition en couleur accent
  }
}

// Style spécifique pour les messages de chargement/erreur quand la réunion n'est pas trouvée
.message-block {
  text-align: center;
  p {
    margin-bottom: $space-md;
    font-size: $font-size-lg;
  }
  // Ce conteneur .back-button-container est juste là pour appliquer la marge au bouton de retour
  .back-button-container {
    margin-top: $space-md;
  }
}

// Styles du formulaire (identiques à NewMeetingView)
form {
  display: grid;
  gap: $space-md;
}

.form-group {
  label {
    display: block;
    margin-bottom: $space-xs;
    font-weight: bold;
    color: $color-text-900;
    font-size: $font-size-base;

    .required {
      color: $color-error;
      margin-left: $space-xs / 2;
    }
  }

  input[type="text"],
  input[type="date"],
  input[type="time"],
  textarea {
    width: 100%;
    padding: $space-sm;
    border: 1px solid $color-primary-100;
    border-radius: $border-radius-sm;
    font-size: $font-size-base;
    font-family: $font-body;
    color: $color-text-600;
    background-color: $color-background;
    transition: border-color $transition-duration-short ease-in-out,
      box-shadow $transition-duration-short ease-in-out;

    &:focus {
      outline: none;
      border-color: $color-primary-500;
      box-shadow: 0 0 0 3px rgba($color-primary-500, 0.2);
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background-color: lighten($color-background, 2%);
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }
}

.form-actions {
  display: flex;
  gap: $space-md;
  margin-top: $space-lg;
  justify-content: flex-end;
}

.error-message {
  color: $color-error;
  text-align: center;
  margin-top: $space-md;
}
</style>
